import React, { useState, useEffect, useCallback } from "react";
import Loader from "../../components/loader/Loader";
import { Button, Switch, Box } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { grey } from "@mui/material/colors";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import axios from "axios";
import AddFlatPrice from "./AddFlatPrice";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeFlatpriceList,
} from "../../redux/counterSlice";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import { usePermission } from "../../hooks/usePermission";

const FlatPrice = () => {
  const dispatch = useDispatch();
  const { reset } = useForm();
  const permissions = usePermission("flatPrice");

  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("flatPrice", 10);
  const tableHeight = useTableHeight();

  const flatPriceList = useSelector(
    (state) => state.counter.flatPriceList || []
  );
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const loadInitialData = useCallback(async () => {
    try {
      setLoader(true);
      const response = await axios.get("/offerFixedPrice");
      dispatch(storeFlatpriceList(response.data?.data || []));
    } catch (err) {
      toast.error(err?.message || "Failed to fetch flat price list");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  }, [dispatch, page, rowsPerPage]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const toggleStatus = (id, currentStatus) => {
    const endpoint =
      currentStatus === 1
        ? `/deActivateOfferFixedPrice/${id}`
        : `/activateOfferFixedPrice/${id}`;
    setLoader(true);
    axios
      .get(endpoint)
      .then(() => {
        toast.success(
          currentStatus === 1
            ? "Flat price deactivated"
            : "Flat price activated"
        );
        loadInitialData();
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to toggle status");
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  const deleteClickHandler = async (id) => {
    try {
      const response = await axios.delete(`/offerFixedPrice/${id}`);
      if (response.data?.error) {
        let errDesc = response.data?.message || "Something went wrong";
        if (response.data?.data) {
          errDesc = Object.values(response.data.data).join("\n");
        }
        dispatch(showModal({ msg: errDesc, error: true }));
      } else {
        toast.success(response.data?.message || "Deleted successfully");
        loadInitialData();
        handleClose();
      }
    } catch (error) {
      dispatch(
        showModal({
          msg:
            error?.response?.data?.message ||
            error?.message ||
            "Server Error",
          error: true,
        })
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  const editClickHandler = (item) => {
    setSelectedRow({ ...item });
    setOpen(true);
  };

  const filteredFlatPriceList = flatPriceList
    .filter((item) =>
      String(item.fixed_price)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    )
    .sort((a, b) => +a.fixed_price - +b.fixed_price);

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{+dataIndex + 1}</p>,
        filter: false,
      },
    },
    {
      name: "fixed_price",
      label: "Fixed Price",
      options: {
        customBodyRenderLite: (dataIndex) =>
          filteredFlatPriceList[dataIndex]?.fixed_price || "-",
      },
    },
    // {
    //   name: "active_status",
    //   label: "Status",
    //   options: {
    //     customBodyRenderLite: (dataIndex) => {
    //       const item = filteredFlatPriceList[dataIndex];
    //       const status = item?.active_status;

    //       return (
    //         <Switch
    //           size="small"
    //           color={status === 1 ? "success" : "error"}
    //           checked={status === 1}
    //           onChange={() =>
    //             dispatch(
    //               showConfirmModal({
    //                 msg: status === 1 ? "Deactivate" : "Activate",
    //                 action: () => toggleStatus(item.id, status),
    //               })
    //             )
    //           }
    //           style={{ marginBottom: "5px" }}
    //         />
    //       );
    //     },
    //     filter: true,
    //     filterOptions: {
    //       names: ["Active", "Inactive"],
    //       logic(value, filters) {
    //         if (filters.length) {
    //           if (filters.includes("Active") && +value === 1) return false;
    //           if (filters.includes("Inactive") && +value === 0) return false;
    //           return true;
    //         }
    //         return false;
    //       },
    //     },
    //   },
    // },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            label: "Actions",
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => {
                const item = filteredFlatPriceList[dataIndex];
                const status = item?.active_status;

                return (
                  <div className="actionBtns" style={{ gap: "8px" }}>
                    {permissions?.edit && (
                      <Button
                        disabled={status === 0}
                        aria-label="edit"
                        onClick={() => editClickHandler(item)}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{ color: status === 0 ? grey[600] : "secondary" }}
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={status === 0}
                        aria-label="delete"
                        onClick={() =>
                          dispatch(
                            showConfirmModal({
                              msg: "Delete",
                              action: () => deleteClickHandler(item.id),
                            })
                          )
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: status === 0 ? grey[600] : "red" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: true,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText: searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Flat Price</h4>
          <MUIDataTable
            data={filteredFlatPriceList}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSelectedRow(null);
                    setOpen(true);
                  }}
                  startIcon={<AddIcon />}
                >
                  New Flat Price
                </Button>
              ) : null
            }
          />
        </>
      )}

      <AddFlatPrice
        open={open}
        handleClose={handleClose}
        selectedRow={selectedRow}
        loadInitialData={loadInitialData}
      />
    </>
  );
};

export default FlatPrice;